import { useCallback, useEffect, useRef, useState } from "react";
import { audioBlobToWavBase64 } from "./audioToWav";
import { audioBlobToMp3Base64 } from "./audioToMp3";
import { audioBlobToFlacBase64 } from "./audioToFlac";

// Mic recording hook for the dictation button. Manages getUserMedia +
// MediaRecorder and returns the captured audio as base64 in the
// user-selected upload format (0020: wav | flac | mp3 | aac | webm) for the
// /ai/transcribe route.
//
// Capture vs output:
//   - wav/flac/mp3: capture stays webm/opus (best MediaRecorder codec) and
//     the OUTPUT is converted at finalize() via decodeAudioData → 16kHz mono
//     PCM → the format's encoder.
//   - aac: MediaRecorder captures natively into an mp4/AAC container
//     (audio/mp4;codecs=mp4a.40.2) when the browser supports it — the raw
//     blob is uploaded as-is with format "aac" (the backend maps aac → mp4
//     for OpenRouter). If NEITHER mp4 mimeType is supported, the hook falls
//     back to webm/opus capture + WAV conversion and reports the fallback
//     via the onAacFallback callback so the button can toast it.
//   - webm: the raw webm/opus blob is uploaded as-is (pass-through — the
//     user explicitly wants this even though Azure/MAI rejects it).
//
// State machine: idle -> initializing (awaiting mic permission) ->
// recording -> transcribing (stop() called or 2-min auto-stop, blob
// converting / caller transcribing) -> idle (caller calls reset()).
//
// The hook does NOT reset to idle by itself after a recording: the caller
// owns the transcription round-trip and calls reset() when the whole flow
// (record -> transcribe -> insert) completes or errors. This keeps the
// button in the "transcribing" visual state for the entire API call.
//
// The 2-minute cap bounds the payload for every format (uniform; the
// provider-side timeout is the binding constraint, not payload size).
// When the cap fires, the recording is NOT discarded — it is delivered via
// the onAutoStop callback passed to start(), so the caller can transcribe
// it exactly like a user-initiated stop.

export type DictationState = "idle" | "initializing" | "recording" | "transcribing";

export type DictationFormat = "wav" | "flac" | "mp3" | "aac" | "webm";

const MAX_RECORDING_MS = 2 * 60 * 1000;

export interface DictationRecording {
  data: string; // base64 audio (no data: prefix)
  format: string; // the encoded/upload format actually sent
}

export interface UseDictationRecorderOptions {
  /** Upload format picked in Settings (0020). Default "wav". */
  format?: DictationFormat;
  /**
   * Fired once when format==="aac" but the browser can't capture AAC
   * (neither audio/mp4;codecs=mp4a.40.2 nor audio/mp4 is supported) and the
   * hook silently fell back to WAV. Lets the button toast the substitution.
   */
  onAacFallback?: () => void;
}

export function useDictationRecorder(opts?: UseDictationRecorderOptions) {
  const formatRef = useRef<DictationFormat>(opts?.format ?? "wav");
  formatRef.current = opts?.format ?? "wav";
  const onAacFallbackRef = useRef<UseDictationRecorderOptions["onAacFallback"]>(opts?.onAacFallback);
  onAacFallbackRef.current = opts?.onAacFallback;

  const [state, setState] = useState<DictationState>("idle");
  const [elapsed, setElapsed] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);
  // Whether the current capture is a native AAC/mp4 recording (format==="aac"
  // and the browser supported an mp4 mimeType). When false + format==="aac",
  // finalize() converts to WAV instead of uploading the webm blob as "aac".
  const nativeAacRef = useRef(false);
  const stopResolveRef = useRef<((r: DictationRecording) => void) | null>(null);
  const stopRejectRef = useRef<((e: Error) => void) | null>(null);
  const onAutoStopRef = useRef<((r: DictationRecording) => void) | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    clearTimer();
    recorderRef.current = null;
    chunksRef.current = [];
    nativeAacRef.current = false;
    // Stop all mic tracks so the browser's recording indicator goes away.
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
  }, [clearTimer]);

  // Release the mic + clear the timer on unmount.
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Shared blob -> base64 conversion in the selected format, invoked from
  // onstop for BOTH the user-initiated stop (resolves the stop() promise)
  // and the 2-minute auto-stop (invokes the onAutoStop callback). State
  // stays "transcribing" — the caller resets to idle when its flow finishes.
  //
  // The conversion (decode + resample + encode) is async; the resolve/
  // reject plumbing is promise-based so it composes naturally.
  const finalize = useCallback(
    (blob: Blob) => {
      const format = formatRef.current;
      let convert: Promise<{ base64: string; format: string }>;
      if (format === "aac" && nativeAacRef.current) {
        // Native mp4/AAC capture — upload the raw container (backend maps
        // aac → mp4 for OpenRouter's input_audio.format).
        convert = blobToBase64(blob).then((base64) => ({ base64, format: "aac" }));
      } else if (format === "webm") {
        // Deliberate pass-through — the user wants raw webm even though
        // Azure-backed models (MAI-Transcribe 2) reject it.
        convert = blobToBase64(blob).then((base64) => ({ base64, format: "webm" }));
      } else if (format === "mp3") {
        convert = audioBlobToMp3Base64(blob);
      } else if (format === "flac") {
        convert = audioBlobToFlacBase64(blob);
      } else {
        // wav — also the fallback when format==="aac" but the capture
        // wasn't native mp4 (nativeAacRef false).
        convert = audioBlobToWavBase64(blob);
      }
      convert
        .then(({ base64, format: outFormat }) => {
          cleanup();
          const recording: DictationRecording = { data: base64, format: outFormat };
          if (stopResolveRef.current) {
            stopResolveRef.current(recording);
          } else {
            onAutoStopRef.current?.(recording);
          }
          stopResolveRef.current = null;
          stopRejectRef.current = null;
        })
        .catch(() => {
          cleanup();
          stopRejectRef.current?.(new Error("Could not convert recording"));
          stopResolveRef.current = null;
          stopRejectRef.current = null;
        });
    },
    [cleanup],
  );

  const start = useCallback(
    async (opts2?: { onAutoStop?: (recording: DictationRecording) => void }): Promise<void> => {
      if (state !== "idle") return;
      setState("initializing");
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        streamRef.current = stream;

        const format = formatRef.current;
        const supported = (t: string) =>
          typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(t);

        let mimeType: string | undefined;
        nativeAacRef.current = false;
        if (format === "aac") {
          // Native AAC capture: prefer the explicit codec string, then the
          // bare container. If neither is supported, fall back to the normal
          // webm/opus capture and let finalize() convert to WAV — surfacing
          // the fallback so the button can toast "AAC not supported here".
          if (supported("audio/mp4;codecs=mp4a.40.2")) {
            mimeType = "audio/mp4;codecs=mp4a.40.2";
            nativeAacRef.current = true;
          } else if (supported("audio/mp4")) {
            mimeType = "audio/mp4";
            nativeAacRef.current = true;
          } else {
            onAacFallbackRef.current?.();
          }
        }
        if (!mimeType) {
          // wav/flac/mp3/webm (and the AAC fallback): capture webm/opus —
          // the best MediaRecorder codec; finalize() converts or passes
          // through as needed.
          if (supported("audio/webm;codecs=opus")) {
            mimeType = "audio/webm;codecs=opus";
          } else if (supported("audio/ogg;codecs=opus")) {
            mimeType = "audio/ogg;codecs=opus";
          } else if (supported("audio/mp4")) {
            mimeType = "audio/mp4";
          }
        }
        // MediaRecorder defaults to something reasonable when mimeType is left
        // undefined — decodeAudioData handles whatever it produced.

        const recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
        recorderRef.current = recorder;
        chunksRef.current = [];
        onAutoStopRef.current = opts2?.onAutoStop ?? null;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
          setState("transcribing");
          finalize(blob);
        };

        recorder.start(250); // timeslice so chunks accumulate during recording
        startedAtRef.current = Date.now();
        setElapsed(0);
        clearTimer();
        timerRef.current = setInterval(() => {
          const ms = Date.now() - startedAtRef.current;
          setElapsed(Math.floor(ms / 1000));
          if (ms >= MAX_RECORDING_MS) {
            // Auto-stop at the 2-minute cap — onstop delivers the recording
            // via onAutoStop (the stop() promise is not pending in this path).
            const r = recorderRef.current;
            if (r && r.state !== "inactive") r.stop();
          }
        }, 500);
        setState("recording");
      } catch (e) {
        cleanup();
        setState("idle");
        const err = e as DOMException;
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          throw new Error("Microphone permission denied — allow mic access to dictate");
        }
        if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
          throw new Error("No microphone found on this device");
        }
        throw new Error(err.message || "Could not start recording");
      }
    },
    [state, cleanup, clearTimer, finalize],
  );

  const stop = useCallback((): Promise<DictationRecording> => {
    return new Promise<DictationRecording>((resolve, reject) => {
      const r = recorderRef.current;
      if (!r || r.state === "inactive") {
        reject(new Error("Not recording"));
        return;
      }
      stopResolveRef.current = resolve;
      stopRejectRef.current = reject;
      r.stop(); // fires onstop -> finalize -> resolves with base64
    });
  }, []);

  // Caller-owned reset back to idle — call when the transcribe+insert flow
  // (or its error path) is finished.
  const reset = useCallback(() => {
    cleanup();
    setState("idle");
    setElapsed(0);
    onAutoStopRef.current = null;
  }, [cleanup]);

  return { state, elapsed, start, stop, reset, cleanup };
}

// Raw blob → base64 (no data: prefix), chunked — same pattern as the
// audioToWav.ts base64 loop, reused for the aac/webm pass-through paths.
function blobToBase64(blob: Blob): Promise<string> {
  return blob.arrayBuffer().then((buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  });
}
